import Area from '@/components/Area';
import { Canvas, MeshBasicMaterialProps, MeshProps } from '@react-three/fiber';
import * as React from 'react';

import * as THREE from 'three';
import {
  createContext,
  useMemo,
  useRef,
  useState,
  useContext,
  useLayoutEffect,
  forwardRef,
  useEffect,
  ReactNode,
} from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { QuadraticBezierLine, Text, Cone, MapControls } from '@react-three/drei';
import { useDrag } from '@use-gesture/react';
import { EventHandlers } from '@react-three/fiber/dist/declarations/src/core/events';

interface NodeState {
  position: THREE.Vector3;
  connectedTo: React.MutableRefObject<THREE.Mesh | null>[];
}

const context = createContext<React.Dispatch<React.SetStateAction<NodeState[]>>>(() => {});

type CircleProps = MeshProps &
  Pick<MeshBasicMaterialProps, 'opacity' | 'color'> & {
    radius?: number;
    segments?: number;
  };
const Circle = React.forwardRef<THREE.Mesh, CircleProps>(
  ({ children, opacity = 1, radius = 0.05, segments = 32, color = '#ff1050', ...props }, ref) => (
    <mesh ref={ref} {...props}>
      <circleGeometry args={[radius, segments]} />
      <meshBasicMaterial transparent={opacity < 1} opacity={opacity} color={color} />
      {children}
    </mesh>
  )
);

interface NodeProps {
  color?: string;
  name?: string;
  connectedTo?: React.MutableRefObject<THREE.Mesh | null>[];
  position?: [number, number, number];
}

const Node = forwardRef<THREE.Mesh, NodeProps>(
  ({ color = 'black', name, connectedTo = [], position = [0, 0, 0], ...props }, ref) => {
    const setNodes = useContext(context);
    const defaultControls = useThree((state) => state.controls) as unknown as { enabled: boolean };
    const size = useThree(({ size }) => size);
    const camera = useThree(({ camera }) => camera);
    // const invalidate = useThree(({ invalidate }) => invalidate);
    const [xy, setXy] = useState<[number, number]>();
    const [pos, setPos] = useState(() => new THREE.Vector3(...position));
    const state = useMemo(() => ({ position: pos, connectedTo }), [pos, connectedTo]);
    // Register this node on mount, unregister on unmount
    useLayoutEffect(() => {
      setNodes((nodes) => [...nodes, state]);
      return () => void setNodes((nodes) => nodes.filter((n) => n !== state));
    }, [state, setNodes]);
    // Drag n drop, hover
    const [hovered, setHovered] = useState(false);
    useEffect(() => void (document.body.style.cursor = hovered ? 'grab' : 'auto'), [hovered]);
    const bind = useDrag(({ down, xy: [x, y] }) => {
      document.body.style.cursor = down ? 'grabbing' : 'grab';
      // console.log(size);
      setXy([x, y]);
      setPos(
        new THREE.Vector3(((x - size.left) / size.width) * 2 - 1, -((y - size.top) / size.height) * 2 + 1, 0)
          .unproject(camera)
          .multiply(new THREE.Vector3(1, 1, 0))
          .clone()
      );
    });
    const handlers = React.useMemo(() => {
      const {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        // onLostPointerCapture
      } = bind();

      return {
        onPointerDown(e) {
          defaultControls.enabled = false;
          e.stopPropagation();
          onPointerDown!(e as any);
        },
        onPointerMove(e) {
          // defaultControls.enabled = false;
          e.stopPropagation();
          onPointerMove!(e as any);
        },
        onPointerUp(e) {
          defaultControls.enabled = true;
          e.stopPropagation();
          onPointerUp!(e as any);
        },
        onPointerCancel(e) {
          defaultControls.enabled = true;
          e.stopPropagation();
          onPointerCancel!(e as any);
        },
      } satisfies EventHandlers;
    }, [defaultControls]);
    return (
      <Circle ref={ref} {...handlers} opacity={0.2} radius={0.5} segments={32} color={color} position={pos} {...props}>
        <Circle
          radius={0.25}
          segments={32}
          position={[0, 0, 0.1]}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          color={hovered ? '#ff1050' : color}
        >
          <Text position={[0, 0, 1]} fontSize={0.25}>
            {name}
          </Text>
          {xy && (
            <Text position={[0, -1, 1]} fontSize={0.25} color="black">
              ({xy[0].toFixed(2) + ', ' + xy[1].toFixed(2)})
            </Text>
          )}
        </Circle>
      </Circle>
    );
  }
);

type NodesProps = {
  children: ReactNode;
};
function Nodes({ children }: NodesProps) {
  const group = useRef<THREE.Group>(null);
  const refArrayHelper = useRef<THREE.ArrowHelper>(null);
  const [nodes, setNodes] = useState<NodeState[]>([]);
  const lines = useMemo(() => {
    const arrowHelper = refArrayHelper.current;
    const lines: {
      start: THREE.Vector3;
      end: THREE.Vector3;
      direction: THREE.Vector3;
      arrowPosition: THREE.Vector3;
      arrowRotation: THREE.Euler;
    }[] = [];
    for (let node of nodes)
      node.connectedTo
        .map((ref) => [node.position, ref.current?.position])
        .filter(([start, end]) => start && end)
        .forEach(([start, end]) => {
          const startPos = (start as THREE.Vector3).clone().add(new THREE.Vector3(0.35, 0, 0));
          const endPos = (end as THREE.Vector3).clone().add(new THREE.Vector3(-0.35, 0, 0));
          const direction = endPos.clone().sub(startPos).normalize();

          const arrowPosition = endPos.clone().sub(direction.clone().multiplyScalar(0.1));
          // Calculate rotation for arrow to point along the line
          const arrowRotation = new THREE.Euler();
          const up = new THREE.Vector3(0, 1, 0);
          const quaternion = new THREE.Quaternion();
          quaternion.setFromUnitVectors(up, direction);
          arrowRotation.setFromQuaternion(quaternion);
          arrowRotation.z += Math.PI / 2; // Adjust for cone orientation

          // if (arrowHelper && start && end) {
          //   const direction = start.clone().sub(end);
          //   arrowHelper.add(new THREE.ArrowHelper(direction, start, 1, 0xff0000));
          // }

          lines.push({
            start: startPos,
            end: endPos,
            direction,
            arrowPosition,
            arrowRotation,
          });
        });
    return lines;
  }, [nodes]);
  useFrame((_, delta) =>
    group.current?.children.forEach((group: any) => {
      if (group.children[0]?.material?.uniforms?.dashOffset) {
        group.children[0].material.uniforms.dashOffset.value -= delta * 10;
      }
    })
  );
  // console.log('Nodes', { lines });
  return (
    <context.Provider value={setNodes}>
      <group ref={group}>
        {lines.map((line, index) => (
          <group key={`line-group-${index}`}>
            <QuadraticBezierLine
              key={`dashed-${index}`}
              start={line.start}
              end={line.end}
              color="black"
              dashed
              dashScale={50}
              gapSize={20}
              lineWidth={2}
            />
            <QuadraticBezierLine
              key={`solid-${index}`}
              start={line.start}
              end={line.end}
              color="black"
              lineWidth={2}
              transparent
              opacity={0.1}
            />
            {/* Arrow at the end */}
            {/* <Cone
              key={`arrow-${index}`}
              position={line.arrowPosition}
              rotation={line.arrowRotation}
              args={[0.08, 0.15, 8]}
            >
              <meshBasicMaterial color="black" />
            </Cone> */}
          </group>
        ))}
      </group>
      {children}
      {lines.map(({ start, end }, index) => (
        <group key={`circle-group-${index}`} position-z={1}>
          <Circle position={start} radius={0.05} segments={32} />
          <Circle position={end} radius={0.05} segments={32} />
        </group>
      ))}
      <arrowHelper ref={refArrayHelper} />
    </context.Provider>
  );
}

export type Props = {};

function App01(props: Props) {
  const {} = props;
  const [[a, b, c, d, e]] = useState(() => [...Array(5)].map(React.createRef<THREE.Mesh>));
  return (
    <Area>
      {(el) => (
        <Canvas eventSource={el} orthographic camera={{ zoom: 30 }}>
          {/* <ambientLight color="#ffffff" /> */}
          {/* <Environment preset={'city'} /> */}
          <Nodes>
            <Node ref={a} name="a" color="#204090" position={[-2, 2, 0]} connectedTo={[b, c, e]} />
            <Node ref={b} name="b" color="#904020" position={[2, -3, 0]} connectedTo={[d, a]} />
            <Node ref={c} name="c" color="#209040" position={[0, 0, 0]} />
            <Node ref={d} name="d" color="#204090" position={[1, -1, 0]} />
            <Node ref={e} name="e" color="#204090" position={[-1, -1, 0]} />
          </Nodes>
          <MapControls domElement={el} enableDamping={false} makeDefault enableRotate={false} screenSpacePanning />
          <gridHelper rotation={[-Math.PI / 2, 0, 0]} />
        </Canvas>
      )}
    </Area>
  );
}

export default React.memo(App01);
